import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { AuditApiService } from '../services/audit-api.service';
import { AuditSettingResponse, AuditSettingUpdateRequest } from '../models/audit.model';
import { ToastService } from '../../../shared/services/toast.service';
import { AuditDisplayService } from '../../../shared/services/audit-display.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { DisableWithoutPermissionDirective } from '../../../shared/directives/disable-without-permission.directive';

@Component({
  selector: 'app-audit-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, HasPermissionDirective, DisableWithoutPermissionDirective],
  templateUrl: './audit-settings.component.html'
})
export class AuditSettingsComponent implements OnInit {
  settings: AuditSettingResponse[] = [];
  loading = false;
  error = '';
  reloading = false;

  // Edit modal
  showEditModal = false;
  editingSetting: AuditSettingResponse | null = null;
  editForm: AuditSettingUpdateRequest = { enabled: true, auditLevel: 'FULL' };
  sensitiveFieldsText = '';
  excludedFieldsText = '';
  saving = false;

  auditLevelOptions: Array<'NONE' | 'BASIC' | 'FULL' | 'DETAILED'> = ['NONE', 'BASIC', 'FULL', 'DETAILED'];

  constructor(
    private auditApi: AuditApiService,
    private toast: ToastService,
    public display: AuditDisplayService
  ) {}

  ngOnInit(): void {
    this.loadSettings();
  }

  loadSettings(): void {
    this.loading = true;
    this.error = '';
    this.auditApi.getSettings().subscribe({
      next: data => {
        this.settings = data;
        this.loading = false;
      },
      error: () => {
        this.error = 'Error al cargar la configuración de auditoría.';
        this.loading = false;
      }
    });
  }

  openEdit(setting: AuditSettingResponse): void {
    this.editingSetting = setting;
    this.editForm = {
      enabled: setting.enabled,
      auditLevel: setting.auditLevel,
      retentionDays: setting.retentionDays,
      sensitiveFields: setting.sensitiveFields ?? [],
      excludedFields: setting.excludedFields ?? []
    };
    this.sensitiveFieldsText = (setting.sensitiveFields ?? []).join(', ');
    this.excludedFieldsText = (setting.excludedFields ?? []).join(', ');
    this.showEditModal = true;
  }

  saveSetting(): void {
    if (!this.editingSetting) return;
    this.saving = true;

    this.editForm.sensitiveFields = this.parseFieldList(this.sensitiveFieldsText);
    this.editForm.excludedFields = this.parseFieldList(this.excludedFieldsText);

    this.auditApi.updateSetting(this.editingSetting.entityName, this.editForm).subscribe({
      next: () => {
        this.saving = false;
        this.showEditModal = false;
        this.loadSettings();
        this.toast.updated('Configuración de auditoría');
      },
      error: () => {
        this.saving = false;
        this.toast.error({ title: 'Error', message: 'Error al guardar la configuración de auditoría.' });
      }
    });
  }

  reloadCache(): void {
    this.reloading = true;
    this.auditApi.reloadSettingsCache().subscribe({
      next: () => {
        this.reloading = false;
        this.toast.success('La configuración de auditoría se recargó y ya está aplicada.');
      },
      error: () => {
        this.reloading = false;
        this.toast.error({ title: 'Error', message: 'No se pudo recargar la caché de auditoría.' });
      }
    });
  }

  getAuditLevelClass(level: string): string {
    switch (level) {
      case 'DETAILED': return 'bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400';
      case 'FULL': return 'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400';
      case 'BASIC': return 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400';
      case 'NONE': return 'bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400';
      default: return 'bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400';
    }
  }

  private parseFieldList(text: string): string[] {
    if (!text.trim()) return [];
    return text.split(',').map(s => s.trim()).filter(s => s.length > 0);
  }
}
